// Componente único del ticket: celda Producto con «+N», chevron que despliega y líneas en tarjetas.
// Sin teléfono encabeza «Sin teléfono» con el accesorio principal y nunca pinta IMEI.
import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { SaleLine, SalePromotionInfo } from '@/api/services/retail-wizard/contract';
import { saleReceipt } from '@/shared/lib/format/saleReceipt';
import type { DataTableColumn, DataTableProps } from '@/shared/patterns/DataTable';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import {
    expandableTicketLines,
    extraLinesLabel,
    lineMatchesCategory,
    NO_PHONE_LABEL,
    ticketHeadline,
    UNCATEGORIZED_LINE,
    type TicketCategory,
    type TicketSale,
} from './ticketSummary';
import { CategoryIcon, lineIcon } from './categoryIcon';
import { SaleLineCard } from '../SaleReceipt/SaleLineCard';
import { toSlotSale } from '../SaleReceipt/saleSlots';
import { TicketLines, ticketLineClass } from './TicketLines';

export type LineIcon = (line: SaleLine) => ReactNode;

/** Íconos chicos de las demás líneas y «+1 accesorio» / «+N». */
export function TicketExtras({
    extras,
    iconOf,
}: {
    extras: readonly SaleLine[];
    iconOf: LineIcon;
}) {
    if (extras.length === 0) return null;
    return (
        <span className="ml-1.5 whitespace-nowrap text-[9.5px] font-normal text-muted-foreground">
            {extras.map((line) => (
                <span
                    key={line.lineId}
                    className="mr-0.5 inline-flex size-3 align-middle [&>svg]:size-full"
                >
                    {iconOf(line)}
                </span>
            ))}
            {extraLinesLabel(extras)}
        </span>
    );
}

/** Chevron que despliega o pliega las líneas del ticket. */
export function TicketToggle({
    open,
    folio,
    onToggle,
}: {
    open: boolean;
    folio: string | null;
    onToggle: () => void;
}) {
    const Chevron = open ? ChevronDown : ChevronRight;
    return (
        <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? 'Ocultar' : 'Ver'} productos del ticket ${folio ?? ''}`.trim()}
            onClick={(event) => {
                event.stopPropagation();
                onToggle();
            }}
            className="inline-flex size-4 items-center justify-center rounded text-muted-foreground hover:text-foreground"
        >
            <Chevron className="size-3.5" aria-hidden />
        </button>
    );
}

/** Ids de las filas desplegadas y cómo alternarlas. */
export function useExpandedRows(): {
    isExpanded: (id: string) => boolean;
    toggle: (id: string) => void;
} {
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
    return {
        isExpanded: (id) => expanded.has(id),
        toggle: (id) => {
            setExpanded((current) => {
                const next = new Set(current);
                if (!next.delete(id)) next.add(id);
                return next;
            });
        },
    };
}

/** Las líneas del ticket como en el detalle de Mis ventas; las de la categoría filtrada, resaltadas. */
export function SaleTicketLines({
    sale,
    lines,
    totalCents,
    category,
}: {
    sale: { saleId: string; folio: string } & SalePromotionInfo;
    lines: SaleLine[];
    totalCents: number;
    category: TicketCategory;
}) {
    const rows = saleReceipt(lines)?.rows;
    if (!rows) return null;
    const slotSale = toSlotSale(sale, lines);
    return (
        <TicketLines folio={sale.folio} totalCents={totalCents}>
            {rows.map((row) => (
                <SaleLineCard
                    key={row.key}
                    row={row}
                    sale={slotSale}
                    linePromotion={undefined}
                    compact
                    className={ticketLineClass(lineMatchesCategory(row.line, category))}
                />
            ))}
        </TicketLines>
    );
}

/** Celda Producto: ícono, nombre de la línea que encabeza, «+N» y, si se pide, su SKU abajo. */
export function SaleTicketProduct({
    sale,
    category,
    saleName,
    saleIcon,
    iconOf = lineIcon,
    showSku = false,
    imei = null,
}: {
    sale: TicketSale;
    category: TicketCategory;
    saleName: string;
    /** Ícono de la venta cuando no se despliega; por omisión, el de su categoría. */
    saleIcon?: ReactNode;
    iconOf?: LineIcon;
    showSku?: boolean;
    /** IMEI del equipo; la venta sin teléfono no lo pinta. */
    imei?: string | null;
}) {
    const headline = ticketHeadline(sale, category, saleName);
    const fallbackIcon = (
        <CategoryIcon category={headline.noPhone ? UNCATEGORIZED_LINE : 'PHONE'} />
    );
    const shownImei = headline.noPhone ? null : imei;
    return (
        <span className="flex min-w-0 items-center gap-1.5">
            <span className="flex-none text-muted-foreground">
                {headline.line ? iconOf(headline.line) : (saleIcon ?? fallbackIcon)}
            </span>
            <span className="min-w-0">
                {headline.noPhone && headline.name !== NO_PHONE_LABEL ? (
                    <span className="block text-[9.5px] text-muted-foreground">
                        {NO_PHONE_LABEL}
                    </span>
                ) : null}
                <span className="block truncate">
                    {headline.name}
                    <TicketExtras extras={headline.extras} iconOf={iconOf} />
                    {headline.extraCount > 0 ? (
                        <span className="ml-1.5 whitespace-nowrap text-[9.5px] font-normal text-muted-foreground">
                            {`+${String(headline.extraCount)}`}
                        </span>
                    ) : null}
                </span>
                {showSku && headline.sku ? (
                    <span className="block truncate font-mono text-[9.5px] text-muted-foreground">
                        {headline.sku}
                    </span>
                ) : null}
                {shownImei ? (
                    <span className="block truncate font-mono text-[9.5px] text-muted-foreground">
                        {shownImei}
                    </span>
                ) : null}
            </span>
        </span>
    );
}

/** Venta de una tabla de tickets desplegables. */
export type ExpandableTicketSale = TicketSale & {
    saleId: string;
    folio: string;
} & SalePromotionInfo;

/** Columna del chevron y props de la tabla para desplegar los tickets de 2+ líneas. */
export function useTicketExpansion<TSale extends ExpandableTicketSale, TSortKey extends string>({
    category,
    totalCentsOf,
}: {
    category: TicketCategory;
    totalCentsOf: (sale: TSale) => number;
}): {
    expandColumn: DataTableColumn<TSale, TSortKey>;
    tableProps: Required<
        Pick<DataTableProps<TSale, TSortKey>, 'onRowClick' | 'rowClassName' | 'renderExpansion'>
    >;
} {
    const { isExpanded, toggle } = useExpandedRows();
    return {
        expandColumn: {
            key: 'expand',
            label: <span className="sr-only">Desplegar</span>,
            width: '22px',
            cellOverflow: 'nowrap',
            cellClassName: () => 'px-1',
            cell: (sale) =>
                expandableTicketLines(sale) ? (
                    <TicketToggle
                        open={isExpanded(sale.saleId)}
                        folio={sale.folio}
                        onToggle={() => {
                            toggle(sale.saleId);
                        }}
                    />
                ) : null,
        },
        tableProps: {
            onRowClick: (sale) => {
                if (expandableTicketLines(sale)) toggle(sale.saleId);
            },
            rowClassName: (sale) =>
                expandableTicketLines(sale) ? undefined : 'cursor-default hover:bg-transparent',
            renderExpansion: (sale) => {
                const lines = expandableTicketLines(sale);
                return lines && isExpanded(sale.saleId) ? (
                    <SaleTicketLines
                        sale={sale}
                        lines={lines}
                        totalCents={totalCentsOf(sale)}
                        category={category}
                    />
                ) : null;
            },
        },
    };
}

/** Venta que pinta el ticket suelto: equipo opcional (sku, marca, modelo e IMEI pueden ser null). */
export type SaleTicketSale = ExpandableTicketSale & {
    brand?: string | null;
    model?: string | null;
    imei?: string | null;
};

/** Ticket fuera de una tabla: la fila con su chevron y, al desplegar, sus líneas. */
export function SaleTicket({
    sale,
    totalCents,
    category = null,
    saleName,
    iconOf,
    showSku = false,
}: {
    sale: SaleTicketSale;
    totalCents: number;
    category?: TicketCategory;
    /** Por omisión, marca y modelo del equipo. */
    saleName?: string;
    iconOf?: LineIcon;
    showSku?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const lines = expandableTicketLines(sale);
    const name =
        saleName ?? itemDisplayName({ sku: sale.sku, brand: sale.brand, model: sale.model });
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex min-w-0 items-center gap-1">
                {lines ? (
                    <TicketToggle
                        open={open}
                        folio={sale.folio}
                        onToggle={() => {
                            setOpen((current) => !current);
                        }}
                    />
                ) : null}
                <SaleTicketProduct
                    sale={sale}
                    category={category}
                    saleName={name}
                    iconOf={iconOf}
                    showSku={showSku}
                    imei={sale.imei ?? null}
                />
            </div>
            {lines && open ? (
                <SaleTicketLines
                    sale={sale}
                    lines={lines}
                    totalCents={totalCents}
                    category={category}
                />
            ) : null}
        </div>
    );
}
